import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import '../styles/addArticleStyle.css';
import BrandService from "../services/BrandService";
import ArticleService from '../services/ArticleService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import log from "../services/logger";
import { buttonStyles, inputStyles, cardStyles } from '../styles/tailwind.classes';

const AddArticle = () => {
    const [brands, setBrands] = useState([]);
    const [isResetForm, setIsResetForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    // Define measurement units for dropdown
    const measurementUnits = ['KG', 'GR', 'PRUT', 'KOM'];

    // Combine Tailwind classes
    const getInputClass = (touched, error) => `${inputStyles.base} ${
        touched && error ? inputStyles.error : inputStyles.default
    }`;

    const getButtonClass = (type, disabled) => `${buttonStyles.base} ${
        disabled ? buttonStyles.disabled : buttonStyles[type]
    }`;

    // Validation schema
    const validationSchema = Yup.object().shape({
        code: Yup.string().required('Šifra artikla je obavezna'),
        barcode: Yup.string().required('Barkod artikla je obavezan'),
        name: Yup.string().required('Naziv artikla je obavezan'),
        unitOfMeasurement: Yup.string().required('Jedinica mere je obavezna'),
        quantityPerTransportPackage: Yup.number().nullable().transform((value, originalValue) => 
            originalValue === '' ? null : value),
        minimumQuantityDemand: Yup.number().nullable().transform((value, originalValue) => 
            originalValue === '' ? null : value),
        startingQuantityDemand: Yup.number().nullable().transform((value, originalValue) => 
            originalValue === '' ? null : value),
        maximumQuantityDemand: Yup.number().nullable().transform((value, originalValue) => 
            originalValue === '' ? null : value),
        brutoMass: Yup.number().nullable().transform((value, originalValue) => 
            originalValue === '' ? null : value),
        wholesalePrice: Yup.number().required('Veleprodajna cena je obavezna'),
        retailPrice: Yup.number().nullable().transform((value, originalValue) => 
            originalValue === '' ? null : value),
        imageSource: Yup.string(),
        pdv: Yup.number().required('PDV je obavezan'),
        isActive: Yup.boolean().required('Status artikla je obavezan'),
        isVisible: Yup.boolean().required('Vidljivost artikla je obavezna'),
        brandName: Yup.string().required('Naziv brenda je obavezan'),
        minimumTemperature: Yup.number().nullable().transform((value, originalValue) => 
            originalValue === '' ? null : value),
        maximumTemperature: Yup.number().nullable().transform((value, originalValue) => 
            originalValue === '' ? null : value),
        expiryDateInMinutes: Yup.number().nullable().transform((value, originalValue) => 
            originalValue === '' ? null : value),
    });

    const formik = useFormik({
        initialValues: {
            code: '',
            barcode: '',
            name: '',
            unitOfMeasurement: 'KG',
            quantityPerTransportPackage: '',
            minimumQuantityDemand: '',
            startingQuantityDemand: '',
            maximumQuantityDemand: '',
            brutoMass: '',
            wholesalePrice: '100',  // Default value
            retailPrice: '120',     // Default value
            imageSource: '',
            pdv: '20',              // Default value
            isActive: true,
            isVisible: true,
            brandName: '',
            minimumTemperature: '',
            maximumTemperature: '',
            expiryDateInMinutes: '',
            articleImage: null
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setIsLoading(true);
                
                // Convert boolean values to strings for API compatibility
                const formData = {
                    ...values,
                    isActive: values.isActive.toString(),
                    isVisible: values.isVisible.toString()
                };
                
                const response = await ArticleService.create(formData, values.brandName);
                
                // If there's an image file, upload it
                if (values.articleImage) {
                    try {
                        await ArticleService.updateImage(
                            response.article_id, 
                            values.code, 
                            values.articleImage
                        );
                    } catch (imageError) {
                        console.error('Error uploading image:', imageError);
                        toast.warning('Artikal je kreiran, ali slika nije uspešno učitana.');
                    }
                }
                
                setIsResetForm(true);
                toast.success('Artikal uspešno kreiran!');
                setImagePreview(null);
            } catch (error) {
                console.error('Greška prilikom kreiranja artikla', error);
                toast.error('Greška prilikom kreiranja artikla!');
            } finally {
                setIsLoading(false);
            }
        },
    });

    // Fetch brands on component mount
    useEffect(() => {
        retrieveBrands();
    }, []);

    // Reset form when isResetForm is true
    useEffect(() => {
        if (isResetForm) {
            formik.resetForm();
            setIsResetForm(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [isResetForm]);

    const retrieveBrands = () => {
        BrandService.getAll()
            .then(response => {
                setBrands(response.data);
            })
            .catch(e => {
                log.info(e);
                toast.error('Greška prilikom učitavanja brendova');
            });
    };

    const handleFileChange = (event) => {
        const file = event.currentTarget.files[0];
        if (file) {
            formik.setFieldValue('articleImage', file);
            
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className={`${cardStyles.base} ${cardStyles.default} max-w-7xl mx-auto p-6 bg-white`}>
                <h2 className="text-2xl font-semibold text-center text-gray-800 mb-8 pb-4 border-b">
                    Dodaj Artikal
                </h2>
                <ToastContainer position="top-right" autoClose={3000} />
                
                <form onSubmit={formik.handleSubmit} className="space-y-8">
                    <div className="space-y-6">
                        {/* Basic Information Section */}
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                Osnovne informacije
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Šifra Artikla *
                                    </label>
                                    <input
                                        id="code"
                                        name="code"
                                        type="text"
                                        className={getInputClass(formik.touched.code, formik.errors.code)}
                                        placeholder="Unesite šifru"
                                        value={formik.values.code}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.code && formik.errors.code && (
                                        <p className="mt-1 text-sm text-red-600">{formik.errors.code}</p>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Barkod Artikla *
                                    </label>
                                    <input
                                        id="barcode"
                                        name="barcode"
                                        type="text"
                                        className={getInputClass(formik.touched.barcode, formik.errors.barcode)}
                                        placeholder="Unesite barkod"
                                        value={formik.values.barcode}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.barcode && formik.errors.barcode && (
                                        <p className="mt-1 text-sm text-red-600">{formik.errors.barcode}</p>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Naziv Artikla *
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    className={getInputClass(formik.touched.name, formik.errors.name)}
                                    placeholder="Unesite naziv artikla"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                {formik.touched.name && formik.errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{formik.errors.name}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Brend *
                                    </label>
                                    <div className="relative">
                                        <select
                                            id="brandName"
                                            name="brandName"
                                            className={getInputClass(formik.touched.brandName, formik.errors.brandName)}
                                            value={formik.values.brandName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        >
                                            <option value="">Izaberite brend</option>
                                            {brands.map((brand, index) => (
                                                <option 
                                                    key={index} 
                                                    value={brand.brandName}
                                                >
                                                    {brand.brandName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {formik.touched.brandName && formik.errors.brandName && (
                                        <p className="mt-1 text-sm text-red-600">{formik.errors.brandName}</p>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Jedinica mere *
                                    </label>
                                    <div className="relative">
                                        <select
                                            id="unitOfMeasurement"
                                            name="unitOfMeasurement"
                                            className={getInputClass(formik.touched.unitOfMeasurement, formik.errors.unitOfMeasurement)}
                                            value={formik.values.unitOfMeasurement}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        >
                                            {measurementUnits.map((unit) => (
                                                <option 
                                                    key={unit} 
                                                    value={unit}
                                                >
                                                    {unit}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quantities and Packaging Section */}
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                Količine i pakovanje
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Količina po transportnom paketu
                                    </label>
                                    <input
                                        id="quantityPerTransportPackage"
                                        name="quantityPerTransportPackage"
                                        type="number"
                                        step="0.01"
                                        className={getInputClass(false, false)}
                                        placeholder="0.00"
                                        value={formik.values.quantityPerTransportPackage}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Bruto masa (kg)
                                    </label>
                                    <input
                                        id="brutoMass"
                                        name="brutoMass"
                                        type="number"
                                        step="0.01"
                                        className={getInputClass(false, false)}
                                        placeholder="0.00"
                                        value={formik.values.brutoMass}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Minimalna količina
                                    </label>
                                    <input
                                        id="minimumQuantityDemand"
                                        name="minimumQuantityDemand"
                                        type="number"
                                        step="0.01"
                                        className={getInputClass(false, false)}
                                        placeholder="0.00"
                                        value={formik.values.minimumQuantityDemand}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Početna količina
                                    </label>
                                    <input
                                        id="startingQuantityDemand"
                                        name="startingQuantityDemand"
                                        type="number"
                                        step="0.01"
                                        className={getInputClass(false, false)}
                                        placeholder="0.00"
                                        value={formik.values.startingQuantityDemand}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Maksimalna količina
                                    </label>
                                    <input
                                        id="maximumQuantityDemand"
                                        name="maximumQuantityDemand"
                                        type="number"
                                        step="0.01"
                                        className={getInputClass(false, false)}
                                        placeholder="0.00"
                                        value={formik.values.maximumQuantityDemand}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Storage Conditions Section */}
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                Uslovi čuvanja
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Minimalna temperatura (°C)
                                    </label>
                                    <input
                                        id="minimumTemperature"
                                        name="minimumTemperature"
                                        type="number"
                                        step="0.1"
                                        className={getInputClass(false, false)}
                                        placeholder="0.0"
                                        value={formik.values.minimumTemperature}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Maksimalna temperatura (°C)
                                    </label>
                                    <input
                                        id="maximumTemperature"
                                        name="maximumTemperature"
                                        type="number"
                                        step="0.1"
                                        className={getInputClass(false, false)}
                                        placeholder="0.0"
                                        value={formik.values.maximumTemperature}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Rok trajanja (minuti)
                                    </label>
                                    <input
                                        id="expiryDateInMinutes"
                                        name="expiryDateInMinutes"
                                        type="number"
                                        className={getInputClass(false, false)}
                                        placeholder="0"
                                        value={formik.values.expiryDateInMinutes}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                            </div>
                        </div>
                        
                        {/* Image and Visibility Section */}
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                Slika i vidljivost
                            </h3>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        URL slike
                                    </label>
                                    <input
                                        id="imageSource"
                                        name="imageSource"
                                        type="text"
                                        className={getInputClass(false, false)}
                                        placeholder="https://example.com/image.jpg"
                                        value={formik.values.imageSource}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Učitaj sliku
                                    </label>
                                    <input
                                        id="articleImage"
                                        name="articleImage"
                                        type="file"
                                        accept="image/*"
                                        className="w-full"
                                        onChange={handleFileChange}
                                        ref={fileInputRef}
                                    />
                                    {imagePreview && (
                                        <div className="image-preview">
                                            <img src={imagePreview} alt="Preview" />
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Status artikla *
                                    </label>
                                    <div className="toggle-container">
                                        <label className="toggle">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={formik.values.isActive}
                                                onChange={() => formik.setFieldValue('isActive', !formik.values.isActive)}
                                            />
                                            <span className="slider"></span>
                                        </label>
                                        <span className="text-sm font-medium text-gray-700">
                                            {formik.values.isActive ? 'Aktivan' : 'Neaktivan'}
                                        </span>
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Vidljivost artikla *
                                    </label>
                                    <div className="toggle-container">
                                        <label className="toggle">
                                            <input
                                                type="checkbox"
                                                name="isVisible"
                                                checked={formik.values.isVisible}
                                                onChange={() => formik.setFieldValue('isVisible', !formik.values.isVisible)}
                                            />
                                            <span className="slider"></span>
                                        </label>
                                        <span className="text-sm font-medium text-gray-700">
                                            {formik.values.isVisible ? 'Vidljiv' : 'Nevidljiv'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Hidden Price Section (with default values) */}
                        <div className="hidden">
                            <input
                                type="hidden"
                                name="wholesalePrice"
                                value={formik.values.wholesalePrice}
                            />
                            <input
                                type="hidden"
                                name="retailPrice"
                                value={formik.values.retailPrice}
                            />
                            <input
                                type="hidden"
                                name="pdv"
                                value={formik.values.pdv}
                            />
                        </div>
                        
                        {/* Form Actions */}
                        <div className="flex justify-end space-x-4 pt-4 border-t">
                            <button
                                type="button"
                                className={getButtonClass('secondary', isLoading)}
                                onClick={() => formik.resetForm()}
                                disabled={isLoading}
                            >
                                Poništi
                            </button>
                            <button
                                type="submit"
                                className={getButtonClass('primary', isLoading)}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Kreiranje...' : 'Dodaj artikal'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddArticle;
